/**
 * Home's pure builders (Figma 11 · Agency · The morning). No React, no fetches:
 * every function takes what the reads returned plus `now`, so the live hook and
 * the fixture harness compose the same sentences.
 */
import { stageOf } from "../content-calendar/month/contentStage";
import type { CalendarMonthPlanWithSlots, CalendarPlanSlot } from "@/types/contentCalendar.type";
import type { FieldReport } from "@/types/fieldReport.type";
import type { MarketSignal } from "@/types/marketSignal.type";
import type { SocialConnection } from "../settings/api/useSocialConnections";
import type { StageFact } from "../data/universeLayout";
import type {
  HomeActivity,
  HomeCall,
  HomeLearned,
  HomeMonthBucket,
  HomeMonthRow,
} from "./home.types";

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS_SHORT = MONTHS.map((m) => m.slice(0, 3));

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);
const ms = (iso: string | null | undefined) => (iso ? Date.parse(iso) : NaN);

function startOfDay(t: number): number {
  const d = new Date(t);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** "SINCE 9:40", "SINCE YESTERDAY", "SINCE THURSDAY", "SINCE 2 OCT"; first visit "THIS WEEK". */
export function sinceLabel(since: number, now: number, firstVisit: boolean): string {
  if (firstVisit) return "THIS WEEK";
  const days = Math.round((startOfDay(now) - startOfDay(since)) / DAY);
  const d = new Date(since);
  if (days <= 0) return `SINCE ${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;
  if (days === 1) return "SINCE YESTERDAY";
  if (days < 7) return `SINCE ${WEEKDAYS[d.getDay()].toUpperCase()}`;
  return `SINCE ${d.getDate()} ${MONTHS_SHORT[d.getMonth()].toUpperCase()}`;
}

/** "Tue", "yesterday", "today", "2 Oct" — the day something happened, in a meta line. */
function dayWord(t: number, now: number): string {
  const days = Math.round((startOfDay(now) - startOfDay(t)) / DAY);
  const d = new Date(t);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days > 1 && days < 7) return WEEKDAYS[d.getDay()].slice(0, 3);
  if (days === -1) return "tomorrow";
  if (days < 0 && days > -7) return WEEKDAYS[d.getDay()].slice(0, 3);
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
}

/** "9 h", "2 d", "12 m". */
export function ageWord(t: number, now: number): string {
  const diff = Math.max(0, now - t);
  if (diff < HOUR) return `${Math.max(1, Math.round(diff / MINUTE))} m`;
  if (diff < 2 * DAY) return `${Math.round(diff / HOUR)} h`;
  return `${Math.round(diff / DAY)} d`;
}

const timesWord = (n: number) => (n === 1 ? "once" : n === 2 ? "twice" : `${n} times`);

/**
 * "4 posts went live, 11 facts grew, the reporter ran twice, and 3 things need you."
 * A zero drops its clause; a figure not read (null) drops it too, never prints 0.
 */
export function standingLine(
  f: { posts: number | null; facts: number | null; reports: number | null; needs: number | null },
  sinceWord: string,
): string {
  const clauses: string[] = [];
  if (f.posts) clauses.push(`${f.posts} ${plural(f.posts, "post", "posts")} went live`);
  if (f.facts) clauses.push(`${f.facts} ${plural(f.facts, "fact", "facts")} grew`);
  if (f.reports) clauses.push(`the reporter ran ${timesWord(f.reports)}`);
  if (f.needs) clauses.push(`${f.needs} ${plural(f.needs, "thing needs", "things need")} you`);
  if (clauses.length === 0) {
    return f.needs === 0 ? `Nothing new ${sinceWord}, and nothing needs you.` : `Nothing new ${sinceWord}.`;
  }
  if (clauses.length === 1) return `${capital(clauses[0])}.`;
  return `${capital(clauses.slice(0, -1).join(", "))}, and ${clauses[clauses.length - 1]}.`;
}

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/* --------------------------------------------------------------- posts live */

export interface LiveEvent {
  slotId: string;
  title: string;
  platform: string;
  at: number;
}

/** Every publication that went live, newest first. */
export function liveEvents(plans: (CalendarMonthPlanWithSlots | null | undefined)[]): LiveEvent[] {
  const out: LiveEvent[] = [];
  for (const plan of plans) {
    for (const slot of plan?.slots ?? []) {
      for (const p of slot.deliverable?.publications ?? []) {
        if (p.status !== "PUBLISHED") continue;
        const at = ms(p.publishedAt ?? p.scheduledAt);
        if (Number.isFinite(at)) out.push({ slotId: slot.id, title: slot.title, platform: p.platform, at });
      }
    }
  }
  return out.sort((a, b) => b.at - a.at);
}

/** Posts (not publications) that went live after `since`. */
export function postsLiveSince(events: LiveEvent[], since: number): number {
  return new Set(events.filter((e) => e.at > since).map((e) => e.slotId)).size;
}

/* ------------------------------------------------------------------ the month */

export const BUCKET_WORD: Record<HomeMonthBucket, string> = {
  not_started: "not started",
  planning: "planning",
  with_client: "with",
  in_production: "in production",
  live: "live",
};

const BUCKETS: HomeMonthBucket[] = ["not_started", "planning", "with_client", "in_production", "live"];

/** The frame's five words over the calendar's own stages (plan §5 D4). */
export function bucketOf(slot: CalendarPlanSlot): HomeMonthBucket {
  const stage = stageOf(slot);
  if (stage === "posted") return "live";
  if (stage === "client_review" || slot.clientReview?.brief) return "with_client";
  if (stage === "not_started") return "not_started";
  if (stage === "concept") return "planning";
  return "in_production";
}

export function monthRow(
  key: string,
  monthIndex: number,
  plan: CalendarMonthPlanWithSlots | null,
  planStatus: string | null,
  brandName: string,
  now: number,
): HomeMonthRow {
  const name = MONTHS[monthIndex] ?? "";
  if (!plan || planStatus === "DRAFT" || planStatus === null) {
    return {
      key,
      name,
      state: planStatus === "GENERATING" ? "planning" : "not planned",
      right: "",
      sentence: "",
      segments: [],
      total: 0,
    };
  }
  const counts = new Map<HomeMonthBucket, number>();
  for (const slot of plan.slots) counts.set(bucketOf(slot), (counts.get(bucketOf(slot)) ?? 0) + 1);
  const total = plan.slots.length;
  const live = counts.get("live") ?? 0;
  const segments = BUCKETS.map((bucket) => ({ bucket, count: counts.get(bucket) ?? 0 })).filter((s) => s.count > 0);
  const sentence = segments
    .map((s) => `${s.count} ${s.bucket === "with_client" ? `with ${brandName}` : BUCKET_WORD[s.bucket]}`)
    .join(" · ");
  const allLive = total > 0 && live === total;
  const state = planStatus === "GENERATING" ? "planning" : allLive ? "live" : "in production";
  let right = "";
  if (allLive || (total > 0 && nextLiveAt(plan, now) === null && live > 0)) right = `${live} OF ${total}`;
  else {
    const next = nextLiveAt(plan, now);
    if (next !== null) {
      const d = new Date(next);
      right = `NEXT LIVE ${WEEKDAYS[d.getDay()].slice(0, 3).toUpperCase()} ${d.getDate()}`;
    }
  }
  return { key, name, state, right, sentence, segments, total };
}

/** The next moment something in this month is due to go out. */
function nextLiveAt(plan: CalendarMonthPlanWithSlots, now: number): number | null {
  let best: number | null = null;
  const today = startOfDay(now);
  for (const slot of plan.slots) {
    if (stageOf(slot) === "posted") continue;
    const scheduled = (slot.deliverable?.publications ?? [])
      .filter((p) => p.status === "SCHEDULED" || p.status === "READY")
      .map((p) => ms(p.scheduledAt))
      .filter((t) => Number.isFinite(t) && t >= now);
    const at = scheduled.length ? Math.min(...scheduled) : ms(slot.scheduledAt);
    if (!Number.isFinite(at) || at < today) continue;
    if (best === null || at < best) best = at;
  }
  return best;
}

/* ------------------------------------------------------------------ needs you */

const PROVIDER_WORD: Record<string, string> = {
  INSTAGRAM: "Instagram",
  FACEBOOK: "Facebook",
  TIKTOK: "TikTok",
  YOUTUBE: "YouTube",
  SNAPCHAT: "Snapchat",
};

export const NEEDS_SHOWN = 5;

const slotHref = (plan: { year: number; month: number }, slotId: string) =>
  `/dashboard/content-calendar?year=${plan.year}&month=${plan.month}&slot=${encodeURIComponent(slotId)}`;

/**
 * What is waiting on a person: open client sends (Open · Remind), posts whose
 * ideas need a pick (Pick), and accounts that need reconnecting (Reconnect).
 * Returns every row, ordered; the page caps it.
 */
export function needsYou(
  plans: (CalendarMonthPlanWithSlots | null | undefined)[],
  connections: SocialConnection[],
  brandName: string,
  now: number,
): HomeCall[] {
  type Send = {
    plan: CalendarMonthPlanWithSlots;
    kind: "concept" | "brief";
    roundId: string;
    sentAt: number;
    answerBy: number;
    late: boolean;
    slots: CalendarPlanSlot[];
  };
  const sends = new Map<string, Send>();
  const picks: { call: HomeCall; at: number }[] = [];
  for (const plan of plans) {
    if (!plan) continue;
    for (const slot of plan.slots) {
      for (const kind of ["concept", "brief"] as const) {
        const review = slot.clientReview?.[kind];
        if (!review) continue;
        const key = `${kind}:${review.roundId}`;
        const send = sends.get(key) ?? {
          plan,
          kind,
          roundId: review.roundId,
          sentAt: ms(review.sentAt),
          answerBy: ms(review.answerBy),
          late: review.late,
          slots: [],
        };
        send.slots.push(slot);
        sends.set(key, send);
      }
      if (slot.status === "IDEATION") {
        const ideas = slot.ideas ?? [];
        const newest = Math.max(...ideas.map((i) => ms(i.createdAt)).filter(Number.isFinite), ms(slot.updatedAt));
        const day = new Date(ms(slot.scheduledAt));
        const n = ideas.length;
        picks.push({
          at: ms(slot.scheduledAt),
          call: {
            id: `pick:${slot.id}`,
            title: `${n > 0 ? `${capital(numberWord(n))} ${plural(n, "idea", "ideas")}` : "Ideas"} on the ${day.getDate()} ${MONTHS_SHORT[day.getMonth()]} post need a pick`,
            meta: [`Ideation since ${dayWord(newest, now)}`, slot.pillarName ? `pillar ${slot.pillarName}` : null]
              .filter(Boolean)
              .join(" · "),
            second: null,
            action: { kind: "link", label: "Pick", href: slotHref(plan, slot.id) },
          },
        });
      }
    }
  }

  const sendCalls = [...sends.values()]
    .sort((a, b) => Number(b.late) - Number(a.late) || a.sentAt - b.sentAt)
    .map((s): { call: HomeCall; late: boolean } => {
      const month = MONTHS[s.plan.month - 1] ?? "";
      const noun = s.kind === "brief" ? "brief" : "concept";
      const title =
        s.slots.length === 1
          ? `${month}’s ${noun} for ${s.slots[0].title} is with ${brandName}`
          : `${s.slots.length} ${month} ${noun}s are with ${brandName}`;
      const meta = [
        Number.isFinite(s.sentAt) ? `Since ${dayWord(s.sentAt, now)}` : null,
        Number.isFinite(s.answerBy) ? (s.late ? `late since ${dayWord(s.answerBy, now)}` : `answer by ${dayWord(s.answerBy, now)}`) : null,
      ]
        .filter(Boolean)
        .join(" · ");
      return {
        late: s.late,
        call: {
          id: `send:${s.kind}:${s.roundId}`,
          title,
          meta,
          second: { kind: "open", label: "Open", href: slotHref(s.plan, s.slots[0].id) },
          action: { kind: "remind", label: "Remind", planId: s.plan.id, round: s.kind, roundId: s.roundId },
        },
      };
    });

  const reconnects: HomeCall[] = connections
    .filter((c) => c.status === "NEEDS_REAUTH")
    .map((c) => {
      const handle = c.accounts.find((a) => a.username)?.username;
      const since = ms(c.authErrorAt);
      const blocked = c.pendingPublicationCount;
      return {
        id: `reconnect:${c.id}`,
        title: `${PROVIDER_WORD[c.provider] ?? c.provider}${handle ? ` @${handle}` : ""} needs reconnecting`,
        meta: [
          Number.isFinite(since) ? `Since ${dayWord(since, now)}` : null,
          blocked > 0 ? `${blocked} ${plural(blocked, "post cannot", "posts cannot")} go out` : null,
        ]
          .filter(Boolean)
          .join(" · "),
        second: null,
        action: { kind: "link", label: "Reconnect", href: "/dashboard/settings?page=connections" },
      };
    });

  return [
    ...sendCalls.filter((s) => s.late).map((s) => s.call),
    ...reconnects,
    ...sendCalls.filter((s) => !s.late).map((s) => s.call),
    ...picks.sort((a, b) => a.at - b.at).map((p) => p.call),
  ];
}

function numberWord(n: number): string {
  return ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"][n] ?? String(n);
}

/* ---------------------------------------------------------- the brand learned */

export const LEARNED_SHOWN = 4;

export function learned(
  facts: StageFact[],
  signals: MarketSignal[],
  nameOf: (userId: string | null) => string | null,
  now: number,
): HomeLearned[] {
  const rows: (HomeLearned & { at?: number })[] = [];
  for (const f of facts) {
    const at = ms(f.learnedAt);
    if (!Number.isFinite(at)) continue;
    rows.push({
      id: `fact:${f.id}`,
      kind: "fact",
      text: f.desc || f.label,
      meta: [ageWord(at, now), f.concept ? `in ${f.concept}` : null].filter(Boolean).join(" · "),
      href: `/dashboard/brand-universe?fact=${encodeURIComponent(f.id)}`,
      at,
    });
  }
  for (const s of signals) {
    const at = ms(s.acceptedAt);
    if (!Number.isFinite(at) || s.status !== "active") continue;
    const who = nameOf(s.acceptedByUserId);
    const sources = s.groundingObservations?.length ?? 0;
    rows.push({
      id: `signal:${s.id}`,
      kind: "signal",
      text: s.title,
      meta: [
        who ? `Accepted by ${who}` : "Accepted",
        ageWord(at, now),
        sources > 0 ? `${sources} ${plural(sources, "source", "sources")}` : null,
      ]
        .filter(Boolean)
        .join(" · "),
      // The board has no per-signal link; it opens on the live set.
      href: "/dashboard/market-signals",
      at,
    });
  }
  return rows
    .sort((a, b) => (b.at ?? 0) - (a.at ?? 0))
    .slice(0, LEARNED_SHOWN)
    .map((row) => ({ ...row, at: undefined }));
}

/* --------------------------------------------------------------------- latest */

export const LATEST_SHOWN = 5;

const firstName = (name: string | null) => (name ?? "").trim().split(/\s+/)[0] || null;

/** "last night", "this morning", "today", "yesterday", "on Tue". */
function whenWord(t: number, now: number): string {
  const days = Math.round((startOfDay(now) - startOfDay(t)) / DAY);
  const hour = new Date(t).getHours();
  if (days === 0) return hour < 12 ? "this morning" : "today";
  if (days === 1) return hour >= 18 ? "last night" : "yesterday";
  return days < 7 ? `on ${WEEKDAYS[new Date(t).getDay()].slice(0, 3)}` : `on ${dayWord(t, now)}`;
}

function listWords(words: string[]): string {
  if (words.length <= 1) return words.join("");
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

/**
 * The Latest feed, composed from records the page already reads (plan §1e):
 * posts going live, production starting, client decisions, sends, and field
 * reports. There is no activity log behind it.
 */
export function latest(input: {
  plans: (CalendarMonthPlanWithSlots | null | undefined)[];
  live: LiveEvent[];
  reports: FieldReport[];
  signals: MarketSignal[];
  brandName: string;
  nameOf: (userId: string | null) => string | null;
  now: number;
}): HomeActivity[] {
  const { plans, live, reports, signals, brandName, nameOf, now } = input;
  const rows: (HomeActivity & { at?: number })[] = [];
  // "3D", "9H": the figure and its unit run together here (user call, 12 Oct).
  const age = (t: number) => ageWord(t, now).replace(" ", "").toUpperCase();

  // Posts that went live, one line per day.
  const byDay = new Map<number, LiveEvent[]>();
  for (const e of live) {
    const day = startOfDay(e.at);
    byDay.set(day, [...(byDay.get(day) ?? []), e]);
  }
  for (const events of byDay.values()) {
    const posts = new Set(events.map((e) => e.slotId)).size;
    const platforms = [...new Set(events.map((e) => PROVIDER_WORD[e.platform] ?? e.platform))];
    const at = Math.max(...events.map((e) => e.at));
    rows.push({
      id: `live:${startOfDay(at)}`,
      age: age(at),
      text:
        posts === 1
          ? `${events[0].title} went live ${whenWord(at, now)} on ${listWords(platforms)}.`
          : `${posts} posts went live ${whenWord(at, now)} on ${listWords(platforms)}.`,
      at,
    });
  }

  for (const plan of plans) {
    if (!plan) continue;
    const month = MONTHS[plan.month - 1] ?? "";
    for (const slot of plan.slots) {
      const started = ms(slot.productionStartedAt);
      if (Number.isFinite(started)) {
        const who = firstName(nameOf(slot.productionStartedByUserId ?? null));
        rows.push({
          id: `prod:${slot.id}`,
          age: age(started),
          text: `${who ?? "Someone"} moved ${slot.title} into production.`,
          at: started,
        });
      }
      const decision = slot.reviewDecisions?.[0];
      if (decision && !decision.carriedOver) {
        const at = ms(decision.decidedAt);
        if (Number.isFinite(at)) {
          const who = firstName(decision.clientName) ?? brandName;
          rows.push({
            id: `decision:${decision.id}`,
            age: age(at),
            text:
              decision.decision === "APPROVED"
                ? `${who} approved ${slot.title}.`
                : `${who} asked for changes to ${slot.title}.`,
            at,
          });
        }
      }
    }
    const round = plan.reviewRounds?.[0];
    if (round) {
      const at = ms(round.sentAt);
      if (Number.isFinite(at)) rows.push({ id: `round:${round.id}`, age: age(at), text: `${month} went to ${brandName} for approval.`, at });
    }
    const briefSends = new Map<string, { at: number; count: number; title: string }>();
    for (const slot of plan.slots) {
      const brief = slot.clientReview?.brief;
      if (!brief) continue;
      const at = ms(brief.sentAt);
      if (!Number.isFinite(at)) continue;
      const seen = briefSends.get(brief.roundId);
      briefSends.set(brief.roundId, { at, count: (seen?.count ?? 0) + 1, title: slot.title });
    }
    for (const [roundId, send] of briefSends) {
      rows.push({
        id: `briefs:${roundId}`,
        age: age(send.at),
        text:
          send.count === 1
            ? `${month}’s brief for ${send.title} went to ${brandName}.`
            : `${send.count} ${month} briefs went to ${brandName}.`,
        at: send.at,
      });
    }
  }

  for (const report of reports) {
    const at = ms(report.createdAt);
    if (!Number.isFinite(at)) continue;
    const accepted = report.runId
      ? signals.filter((s) => s.sourceRunId === report.runId && s.acceptedAt).length
      : 0;
    rows.push({
      id: `report:${report.id}`,
      age: age(at),
      text: accepted > 0
        ? `A field report was filed: ${accepted} ${plural(accepted, "signal", "signals")} accepted.`
        : "A field report was filed.",
      at,
    });
  }

  return rows
    .filter((r) => (r.at ?? 0) <= now)
    .sort((a, b) => (b.at ?? 0) - (a.at ?? 0))
    .slice(0, LATEST_SHOWN)
    .map((row) => ({ ...row, at: undefined }));
}
